/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import Accordion from '@ion/dls/src/components/accordion/ExpansionPanel';
import {LabelPlacement} from '@ion/dls/src/components/switch/legacy/Switch';
import {LabelChipChooser} from '@ion/dls/src/editors/LabelChipChooser';
import {LabelChooser} from '@ion/dls/src/editors/LabelChooser';
import {LabelToggle} from '@ion/dls/src/editors/LabelToggle';
import Opacity from '@ion/dls/src/maps/components/Opacity';
import {
  LayerSettingsHeader,
  LayerSettingsProps,
  LayerSettingsStyles
} from './LayerSettings';

// Defines the unique identifier for the SC Device Layer
export const SC_DEVICE_LAYER  = 'scDevice';

// defines the styles used by the SCDeviceLayerSettings
const useStyles = makeStyles(theme => ({
  ...LayerSettingsStyles(),
  details : {
    display       : 'flex',
    flexDirection : 'column',
    width         : '96%',
    minWidth      : '225px'
  }
}));

export interface SCDeviceSettingsProps extends LayerSettingsProps {
  onToggleExpand?: Function,
  setSCDeviceAutoInitialized?: Function,
};

/**
 * @SCDeviceLayerSettings
 *
 * The SC Device Layer Settings provides a component which allows users to select
 * specific Current layer related attributes to be displayed within a DLSMap.
 *
 * The SCDeviceLayerSettings component represents the " SC Device Layer" DLS feature:
 * https://www.figma.com/file/srjlHrDuM90sCoaOwpjaDU/01_DLS_Components?node-id=1904%3A13962
 *
 * ```typescript
 *
 *   <SCDeviceLayerSettings onToggle={}
 *     isEnabled={}
 *     map={} />
 *
 * ```
 */
export const SCDeviceLayerSettings = ({
  layers,
  isEnabled,
  isLabellingActive,
  labelContent,
  onToggle,
  expandedLayer,
  onToggleExpand,
  onToggleLabelling,
  onChangeLabelContent,
  setSCDeviceAutoInitialized
}: SCDeviceSettingsProps) => {
  const classes: any              = useStyles();
  const [isSelected, setSelected] = useState(isEnabled);

  const translations = useMemo(() => (
    translate('maps:supplyChainDevice', {returnObjects: true})
  ), []);

  useEffect(() => {
    setSelected(isEnabled);
  }, [isEnabled]);

  const onToggleLayer = useCallback(({target : {checked}}) => {
    setSelected(checked);
    if (!checked) {
      setSCDeviceAutoInitialized?.();
    }
    onToggle(SC_DEVICE_LAYER, checked);
  }, [setSCDeviceAutoInitialized, onToggle]);

  const Details = useMemo(() => (
    <div className={classes.details}>
      <LabelToggle isDarkLabel={false}
        label={translations.labelling}
        labelPlacement={LabelPlacement.START}
        isActive={isLabellingActive}
        onChange={onToggleLabelling} />
      <LabelChipChooser defaultValue={labelContent || 'assetName'}
        entityLabel={'deviceLabel'}
        value={labelContent}
        label={translations.labelContent}
        disabled={!isLabellingActive}
        onChange={onChangeLabelContent} />
    </div>
  ), [isLabellingActive]);

  const Header = useMemo(() => (
    <LayerSettingsHeader label={translations.header}
      isSelected={isSelected}
      onToggle={onToggleLayer} />
  ), [isSelected, layers]);

  return (
    <div className={classes.root}>
      <Grid container direction='row'>
        <Grid item xs={12} md={12}>
          <Paper className={classes.paper}>
            <Accordion children=''
              id={translations.header}
              expandedLayer={expandedLayer}
              onToggleExpand={onToggleExpand}
              header={Header} details={Details}
              detailsRootOverride={classes.accordionDetails} />
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default memo(SCDeviceLayerSettings);
